import type { Lesson } from '@aurea/contracts';
import { Button, Chip, clsx, DoneMark, Pile, Tray } from '@aurea/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Check, Hourglass, MapPin, Paperclip, Plus, Ticket, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LessonDialog } from '../../components/LessonDialog';
import { type LessonDraft, LessonForm } from '../../components/LessonForm';
import { api } from '../../lib/api';
import { fmtDate, fmtDayLong, fmtShortDate, fmtTime, localDay } from '../../lib/time';
import { canManage, isAdmin, isWritable, useMe } from '../_app';

export const Route = createFileRoute('/_app/')({ component: Today });

const UPCOMING_MAX = 6;

function useNow(ms = 30_000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}

function Today() {
  const me = useMe();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const tz = me.timezone;
  const now = useNow();
  const navigate = useNavigate();
  const dash = useQuery({ queryKey: ['dashboard'], queryFn: api.dashboard, refetchInterval: 60_000 });
  const isStudent = me.roles.length === 1 && me.roles[0] === 'student';
  const attention = useQuery({
    queryKey: ['billing', 'attention'],
    queryFn: api.billingAttention,
    enabled: isAdmin(me),
  });
  const myCard = useQuery({
    queryKey: ['student', me.id],
    queryFn: () => api.student(me.id),
    enabled: isStudent,
  });
  const [marking, setMarking] = useState<string | null>(null);
  const [draft, setDraft] = useState<LessonDraft | null>(null);
  const [drawn, setDrawn] = useState<string | null>(null);
  const canMark = isWritable(me) && me.roles.some((r) => r === 'admin' || r === 'teacher');
  const td = localDay(now, tz);
  const todayIso = `${td.y}-${String(td.m).padStart(2, '0')}-${String(td.d).padStart(2, '0')}`;

  const today = dash.data?.today ?? [];
  const isDone = (l: Lesson) => l.status === 'completed';
  const isUnmarked = (l: Lesson) => l.status === 'scheduled' && new Date(l.endsAt) < now;
  const upcoming = today.filter((l) => l.status === 'scheduled' && new Date(l.endsAt) >= now);
  const next = upcoming.find((l) => new Date(l.startsAt) >= now) ?? upcoming[0];
  const done = today.filter((l) => isDone(l) && l.id !== drawn);
  const unmarkedToday = today.filter(isUnmarked);
  const shown = [
    ...(done.length > 2 ? [] : done),
    ...today.filter((l) => l.id === drawn),
    ...unmarkedToday,
    ...upcoming.slice(0, UPCOMING_MAX),
  ].sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const rest = upcoming.slice(UPCOMING_MAX);
  const teachersToday = new Set(today.map((l) => l.teacher.id)).size;
  const openDay = () => navigate({ to: '/schedule', search: { view: 'day' } });

  const ends = new Date(me.school.subscriptionEndsAt);
  const expired = ends < now;

  return (
    <>
      <header className="page-head">
        <div>
          <h1>{fmtDayLong(localDay(now, tz), lang)}</h1>
          <p>
            {t('today.summary', {
              lessons: t('plural.lessons', { count: today.length }),
              done: today.filter(isDone).length,
              teachers: t('plural.teachers', { count: teachersToday }),
            })}
          </p>
        </div>
        <div className="flex-1" />
        {canManage(me) && (
          <Button onClick={() => setDraft({ date: todayIso })} aria-label={t('today.newLesson')}>
            <Plus className="i" />
            <span className="hide-mobile">{t('today.newLesson')}</span>
          </Button>
        )}
      </header>

      <div className="today-layout">
        <section aria-labelledby="today-h">
          <div className="mb-4 flex items-baseline gap-3">
            <h2 id="today-h" className="text-xl font-semibold">
              {t('today.title')}
            </h2>
            <span className="text-sm text-ink-3">{t('today.now', { time: fmtTime(now, tz, lang) })}</span>
          </div>
          {dash.isPending ? (
            <div className="today-grid">
              {[0, 1, 2].map((i) => (
                <div key={i} className="skeleton h-[184px]" />
              ))}
            </div>
          ) : today.length === 0 ? (
            <div className="tray tray-pad">
              <p className="font-semibold">{t('today.empty')}</p>
              <p className="text-sm text-ink-3">{t('today.emptyHint')}</p>
            </div>
          ) : (
            <div className="today-grid">
              {done.length > 2 && (
                <Pile
                  className="t-pile"
                  colors={done.map((l) => l.color)}
                  title={t('today.donePile', { lessons: t('plural.lessons', { count: done.length }) })}
                  sub={`${fmtTime(done[0]!.startsAt, tz, lang)}–${fmtTime(done.at(-1)!.endsAt, tz, lang)} · ${t('today.openDay')}`}
                  onClick={openDay}
                />
              )}
              {shown.map((l) => (
                <TodayCard
                  key={l.id}
                  l={l}
                  tz={tz}
                  now={now}
                  next={l.id === next?.id}
                  unmarked={isUnmarked(l)}
                  drawn={l.id === drawn}
                  onMark={canMark ? () => setMarking(l.id) : undefined}
                />
              ))}
              {rest.length > 0 && (
                <Pile
                  className="t-pile"
                  colors={rest.map((l) => l.color)}
                  title={t('today.morePile', { lessons: t('plural.lessons', { count: rest.length }) })}
                  sub={t('today.openDay')}
                  onClick={openDay}
                />
              )}
            </div>
          )}
        </section>

        <aside className="today-side flex flex-col gap-4" aria-label={t('attention.unmarked')}>
          {canMark && (
            <Tray
              title={t('attention.unmarked')}
              icon={<Check className="i" />}
              count={dash.data?.unmarked.length ?? 0}
            >
              <ul className="rows">
                {dash.data?.unmarked.length === 0 && (
                  <li className="flex-col !items-start !gap-0">
                    <b className="font-semibold">{t('attention.allMarked')}</b>
                    <span className="text-xs text-ink-3">{t('attention.allMarkedHint')}</span>
                  </li>
                )}
                {dash.data?.unmarked.map((l) => (
                  <li key={l.id}>
                    <Chip color={l.color} />
                    <div className="min-w-0 flex-1">
                      <b className="block truncate font-semibold">{l.title}</b>
                      <span className="text-xs text-ink-3">
                        {fmtDate(l.startsAt, tz, lang)} {fmtTime(l.startsAt, tz, lang)} · {l.teacher.name}
                      </span>
                    </div>
                    <button type="button" className="btn-link" onClick={() => setMarking(l.id)}>
                      {t('today.mark')}
                    </button>
                  </li>
                ))}
              </ul>
            </Tray>
          )}
          {isAdmin(me) && (attention.data?.expiring.length ?? 0) > 0 && (
            <Tray
              title={t('billing.expiring')}
              icon={<Hourglass className="i" />}
              count={attention.data?.expiring.length}
            >
              <ul className="rows">
                {attention.data?.expiring.slice(0, 4).map((e) => (
                  <li key={`${e.studentId}${e.packageName}`}>
                    <Chip color={e.color ?? 'sand'} />
                    <div className="min-w-0 flex-1">
                      <Link
                        to="/people/$id"
                        params={{ id: e.studentId }}
                        className="block truncate font-semibold hover:underline"
                      >
                        {e.studentName}
                      </Link>
                      <span className="text-xs text-ink-3">{e.packageName}</span>
                    </div>
                    <span className={clsx('font-semibold', e.left === 0 && 'text-danger')}>
                      {t('billing.left', { left: e.left, total: e.total })}
                    </span>
                  </li>
                ))}
              </ul>
              <Link to="/packages" className="btn-link mt-1 inline-block">
                {t('billing.debtors')}: {attention.data?.debtors.length ?? 0}
              </Link>
            </Tray>
          )}
          {isStudent && (
            <Tray title={t('billing.my')} icon={<Ticket className="i" />}>
              <ul className="rows">
                {myCard.data?.packages
                  .filter((p) => p.status === 'active')
                  .map((p) => (
                    <li key={p.id}>
                      <Chip color={p.color ?? 'sand'} />
                      <div className="min-w-0 flex-1">
                        <b className="block truncate font-semibold">{p.name}</b>
                        <span className="text-xs text-ink-3">
                          {p.validUntil
                            ? t('billing.until', { date: fmtShortDate(p.validUntil, lang) })
                            : t('billing.forever')}
                        </span>
                      </div>
                      <span className="font-semibold">
                        {t('billing.left', { left: p.lessonsTotal - p.lessonsUsed, total: p.lessonsTotal })}
                      </span>
                    </li>
                  ))}
                {myCard.data && !myCard.data.packages.some((p) => p.status === 'active') && (
                  <li className="text-sm text-ink-3">{t('billing.noPackages')}</li>
                )}
              </ul>
            </Tray>
          )}
          {isAdmin(me) && (
            <Tray title={t('attention.subscription')} icon={<Ticket className="i" />}>
              <p className={clsx('text-sm', expired ? 'font-medium text-danger' : 'text-ink-2')}>
                {expired
                  ? t('attention.expired', { date: fmtDate(ends, tz, lang) })
                  : t('attention.plan', { plan: me.school.planName, date: fmtDate(ends, tz, lang) })}
              </p>
              <Meter label={t('attention.teachers')} used={me.school.teachers} max={me.school.maxTeachers} />
              <Meter label={t('attention.students')} used={me.school.students} max={me.school.maxStudents} />
            </Tray>
          )}
        </aside>
      </div>

      <LessonForm draft={draft} tz={tz} onClose={() => setDraft(null)} />
      <LessonDialog
        canCancel={canManage(me)}
        lessonId={marking}
        tz={tz}
        onClose={() => setMarking(null)}
        onDone={(id) => {
          setDrawn(id);
          setTimeout(() => setDrawn(null), 4000);
        }}
      />
    </>
  );
}

function Meter({ label, used, max }: { label: string; used: number; max: number | null }) {
  const { t } = useTranslation();
  return (
    <div className="mt-2 grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-sm text-ink-2">
      <span>{label}</span>
      <span>{max === null ? t('attention.unlimited', { used }) : t('attention.of', { used, max })}</span>
      <span className="col-span-2 h-1.5 overflow-hidden rounded-full bg-desk">
        <i
          className="block h-full rounded-full bg-ink"
          style={{ width: max ? `${Math.min(100, (used / max) * 100)}%` : '0%' }}
        />
      </span>
    </div>
  );
}

function TodayCard({
  l,
  tz,
  now,
  next,
  unmarked,
  drawn,
  onMark,
}: {
  l: Lesson;
  tz: string;
  now: Date;
  next: boolean;
  unmarked: boolean;
  drawn: boolean;
  onMark?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const mins = Math.round((new Date(l.startsAt).getTime() - now.getTime()) / 60_000);
  const done = l.status === 'completed';
  return (
    <article
      className={clsx(
        'paper sticker t-card',
        done && 'done',
        next && 'next',
        unmarked && 'unmarked',
        l.status === 'cancelled' && 'cancelled',
      )}
      data-c={l.color}
    >
      {done && <DoneMark draw={drawn} />}
      <div className="t-time">
        {fmtTime(l.startsAt, tz, lang)}–{fmtTime(l.endsAt, tz, lang)}
      </div>
      <div className="t-title">{l.title}</div>
      <div className="meta">
        {l.groupName
          ? `${t('lesson.group', { name: l.groupName })}, ${t('plural.students', { count: l.participants })}`
          : l.studentName}
      </div>
      <div className="meta">
        <User className="i" aria-hidden="true" />
        {l.teacher.name}
      </div>
      {(l.location || l.onlineUrl) && (
        <div className="meta">
          {l.onlineUrl ? (
            <Paperclip className="i" aria-hidden="true" />
          ) : (
            <MapPin className="i" aria-hidden="true" />
          )}
          {l.onlineUrl ? (
            <a href={l.onlineUrl} target="_blank" rel="noreferrer" className="underline underline-offset-2">
              {t('lesson.online')}
            </a>
          ) : (
            l.location
          )}
        </div>
      )}
      {onMark && (next || unmarked) && (
        <div className="t-foot">
          {next && (
            <span className="now-tag">
              {mins <= 0
                ? t('today.soon')
                : mins < 90
                  ? t('today.inMinutes', { count: mins })
                  : t('today.inHours', { count: Math.round(mins / 60) })}
            </span>
          )}
          {unmarked && <span className="warn-tag">{t('today.unmarked')}</span>}
          <button
            type="button"
            className={clsx('btn', next ? 'btn-on-paper' : 'btn-paper-light')}
            onClick={onMark}
          >
            {next && <Check className="i" />}
            {t('today.mark')}
          </button>
        </div>
      )}
    </article>
  );
}
